import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { startPlaceholderDrag } from "./placeholderDrag";

export interface AiPromptEditorHandle {
  insertPlaceholder: (tag: string) => void;
}

interface AiPromptEditorProps {
  value: string;
  onChange: (next: string) => void;
  // tag -> saved value, only tags with a non-empty value
  placeholderValues: Record<string, string>;
  placeholder?: string;
  className?: string;
}

const TOKEN_RE = /{{\s*([\w.[\]]+)\s*}}/g;
type MarkdownAction =
  | "bold"
  | "italic"
  | "heading"
  | "bullet"
  | "numbered"
  | "quote"
  | "code";

const MARKDOWN_TOOLS: {
  action: MarkdownAction;
  label: string;
  title: string;
}[] = [
  { action: "bold", label: "B", title: "Bold" },
  { action: "italic", label: "I", title: "Italic" },
  { action: "heading", label: "H2", title: "Heading" },
  { action: "bullet", label: "-", title: "Bullet list" },
  { action: "numbered", label: "1.", title: "Numbered list" },
  { action: "quote", label: ">", title: "Quote" },
  { action: "code", label: "`", title: "Inline code" },
];

// Build the chip DOM node for a placeholder. contentEditable=false makes the
// browser treat it as a single, atomic, draggable unit.
const makeChip = (tag: string, val: string): HTMLSpanElement => {
  const span = document.createElement("span");
  span.setAttribute("data-ph", tag);
  span.setAttribute("contenteditable", "false");
  span.setAttribute("draggable", "true");
  // Colors come from theme CSS variables (not Tailwind `dark:` utilities,
  // which don't reliably reach DOM-injected nodes). --foreground gives dark
  // text in light mode and near-white in dark mode; the highlight is a 50%
  // tint of --muted that stays light-gray in light themes and a lighter dark
  // gray in dark themes.
  span.className =
    "inline-block align-baseline rounded px-1 mx-[1px] cursor-grab select-none";
  span.style.color = "var(--foreground)";
  span.style.backgroundColor =
    "color-mix(in srgb, var(--muted) 50%, transparent)";
  span.textContent = val;
  return span;
};

// Render the stored prompt string (with {{ tag }} tokens) into the editor DOM.
// Known tags with a saved value become chips; everything else stays as text.
const renderInto = (
  el: HTMLElement,
  value: string,
  values: Record<string, string>
) => {
  el.innerHTML = "";
  const lines = value.split("\n");
  lines.forEach((line, i) => {
    if (i > 0) el.appendChild(document.createElement("br"));
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    TOKEN_RE.lastIndex = 0;
    while ((m = TOKEN_RE.exec(line))) {
      const [full, tag] = m;
      if (m.index > lastIndex)
        el.appendChild(document.createTextNode(line.slice(lastIndex, m.index)));
      const val = values[tag];
      if (val && val.trim()) el.appendChild(makeChip(tag, val));
      else el.appendChild(document.createTextNode(full));
      lastIndex = m.index + full.length;
    }
    if (lastIndex < line.length)
      el.appendChild(document.createTextNode(line.slice(lastIndex)));
  });
};

// Walk the editor DOM back into the stored prompt string.
const serialize = (root: HTMLElement): string => {
  let out = "";
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent || "";
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.dataset && el.dataset.ph) {
          out += `{{ ${el.dataset.ph} }}`;
        } else if (el.tagName === "BR") {
          out += "\n";
        } else if (el.tagName === "DIV" || el.tagName === "P") {
          if (out !== "" && !out.endsWith("\n")) out += "\n";
          walk(el);
        } else {
          walk(el);
        }
      }
    });
  };
  walk(root);
  return out;
};

const AiPromptEditor = forwardRef<AiPromptEditorHandle, AiPromptEditorProps>(
  ({ value, onChange, placeholderValues, placeholder, className }, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const dropCaretRef = useRef<HTMLDivElement>(null);
    const savedRangeRef = useRef<Range | null>(null);
    // The existing chip currently being dragged within the editor, if any.
    const draggedChipRef = useRef<HTMLElement | null>(null);
    // The last string we serialized out; lets us tell our own edits apart from
    // an external value change so we don't reset the DOM (and the caret) while
    // the user is typing.
    const lastSerializedRef = useRef<string | null>(null);

    const [isEmpty, setIsEmpty] = useState(!value || !value.trim());
    // The placeholder values last painted into the DOM. Lets us repaint chips
    // when the values finish loading, even though the prompt text is unchanged.
    const lastRenderedValuesRef = useRef(placeholderValues);

    // Undo/redo history of the prompt string. Direct DOM edits (chip insert and
    // move) bypass the browser's native undo, so we keep our own stack and
    // handle Ctrl/Cmd+Z ourselves.
    const historyRef = useRef<string[]>([]);
    const histPosRef = useRef(-1);
    const isRestoringRef = useRef(false);

    // (Re)render the DOM only when the value or placeholder values change from
    // the outside, never in response to our own keystrokes.
    useEffect(() => {
      const el = editorRef.current;
      if (!el) return;
      const valuesChanged = lastRenderedValuesRef.current !== placeholderValues;
      if (value === lastSerializedRef.current && !valuesChanged) return;
      renderInto(el, value || "", placeholderValues);
      lastSerializedRef.current = value || "";
      lastRenderedValuesRef.current = placeholderValues;
      setIsEmpty(!value || !value.trim());
      if (historyRef.current.length === 0) {
        historyRef.current = [value || ""];
        histPosRef.current = 0;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, placeholderValues]);

    const pushChange = () => {
      const el = editorRef.current;
      if (!el) return;
      const next = serialize(el);
      lastSerializedRef.current = next;
      setIsEmpty(!next || !next.trim());
      if (
        !isRestoringRef.current &&
        next !== historyRef.current[histPosRef.current]
      ) {
        // Drop any redo branch, then record this state.
        historyRef.current = historyRef.current.slice(
          0,
          histPosRef.current + 1
        );
        historyRef.current.push(next);
        histPosRef.current = historyRef.current.length - 1;
      }
      onChange(next);
    };

    // Repaint the editor from a stored prompt string (used by undo/redo).
    const applyValue = (v: string) => {
      const el = editorRef.current;
      if (!el) return;
      renderInto(el, v, placeholderValues);
      lastSerializedRef.current = v;
      setIsEmpty(!v || !v.trim());
      // Park the caret at the end after a restore.
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
      savedRangeRef.current = r.cloneRange();
      onChange(v);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const isZ = e.key === "z" || e.key === "Z";
      const isY = e.key === "y" || e.key === "Y";
      if (isZ && !e.shiftKey) {
        if (histPosRef.current > 0) {
          e.preventDefault();
          isRestoringRef.current = true;
          histPosRef.current -= 1;
          applyValue(historyRef.current[histPosRef.current]);
          isRestoringRef.current = false;
        }
      } else if ((isZ && e.shiftKey) || isY) {
        if (histPosRef.current < historyRef.current.length - 1) {
          e.preventDefault();
          isRestoringRef.current = true;
          histPosRef.current += 1;
          applyValue(historyRef.current[histPosRef.current]);
          isRestoringRef.current = false;
        }
      }
    };

    const saveSelection = () => {
      const el = editorRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    };

    const insertChipAtRange = (tag: string, range: Range, commit = true) => {
      const val = placeholderValues[tag];
      if (!val) return;
      const chip = makeChip(tag, val);
      range.deleteContents();
      range.insertNode(chip);
      // Drop a caret right after the chip.
      const after = document.createRange();
      after.setStartAfter(chip);
      after.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(after);
      savedRangeRef.current = after.cloneRange();
      if (commit) pushChange();
    };

    const setSelectionAfter = (node: Node) => {
      const range = document.createRange();
      range.setStartAfter(node);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      savedRangeRef.current = range.cloneRange();
    };

    const getCurrentEditorRange = (): Range | null => {
      const el = editorRef.current;
      if (!el) return null;
      const sel = window.getSelection();
      if (
        sel &&
        sel.rangeCount > 0 &&
        el.contains(sel.getRangeAt(0).commonAncestorContainer)
      ) {
        return sel.getRangeAt(0).cloneRange();
      }
      if (
        savedRangeRef.current &&
        el.contains(savedRangeRef.current.commonAncestorContainer)
      ) {
        return savedRangeRef.current.cloneRange();
      }
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      return range;
    };

    const normalizeSelectedLines = (
      selected: string,
      formatter: (line: string, index: number) => string,
      fallback: string
    ) => {
      const text = selected || fallback;
      return text
        .split("\n")
        .map((line, index) => formatter(line, index))
        .join("\n");
    };

    const markdownSnippet = (action: MarkdownAction, selected: string) => {
      switch (action) {
        case "bold":
          return `**${selected || "bold text"}**`;
        case "italic":
          return `_${selected || "italic text"}_`;
        case "heading":
          return normalizeSelectedLines(
            selected,
            (line) => `## ${line.replace(/^#{1,6}\s*/, "") || "Heading"}`,
            "Heading"
          );
        case "bullet":
          return normalizeSelectedLines(
            selected,
            (line) => `- ${line.replace(/^[-*]\s*/, "") || "Item"}`,
            "Item"
          );
        case "numbered":
          return normalizeSelectedLines(
            selected,
            (line, index) =>
              `${index + 1}. ${line.replace(/^\d+\.\s*/, "") || "Item"}`,
            "Item"
          );
        case "quote":
          return normalizeSelectedLines(
            selected,
            (line) => `> ${line.replace(/^>\s*/, "") || "Quoted note"}`,
            "Quoted note"
          );
        case "code":
          return `\`${selected || "code"}\``;
        default:
          return selected;
      }
    };

    const applyMarkdown = (action: MarkdownAction) => {
      const el = editorRef.current;
      const range = getCurrentEditorRange();
      if (!el || !range) return;
      el.focus();
      const selected = range.toString();
      const textNode = document.createTextNode(
        markdownSnippet(action, selected)
      );
      range.deleteContents();
      range.insertNode(textNode);
      setSelectionAfter(textNode);
      pushChange();
    };

    useImperativeHandle(ref, () => ({
      insertPlaceholder: (tag: string) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        let range = savedRangeRef.current;
        if (!range || !el.contains(range.commonAncestorContainer)) {
          range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(false);
        }
        insertChipAtRange(tag, range);
      },
    }));

    // When an existing chip starts being dragged, remember it so we can move
    // it to the drop point.
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const chip = target?.closest?.("[data-ph]") as HTMLElement | null;
      if (chip && editorRef.current?.contains(chip)) {
        e.dataTransfer.setData("text/placeholder-move", chip.dataset.ph || "");
        e.dataTransfer.effectAllowed = "move";
        // Hide the native drag image and draw our own pill following the cursor.
        startPlaceholderDrag(e, chip.textContent || "");
        draggedChipRef.current = chip;
      }
    };

    // Resolve the text position under a screen point to a collapsed Range.
    const caretRangeFromPoint = (x: number, y: number): Range | null => {
      const doc = document as any;
      if (doc.caretRangeFromPoint) return doc.caretRangeFromPoint(x, y);
      if (doc.caretPositionFromPoint) {
        const pos = doc.caretPositionFromPoint(x, y);
        if (pos) {
          const r = document.createRange();
          r.setStart(pos.offsetNode, pos.offset);
          r.collapse(true);
          return r;
        }
      }
      return null;
    };

    // Fade the prompt text/chips during a drag so the blinking insertion line
    // reads clearly against them.
    const setDimmed = (on: boolean) => {
      if (editorRef.current) editorRef.current.style.opacity = on ? "0.25" : "";
    };

    const hideDropCaret = () => {
      if (dropCaretRef.current) dropCaretRef.current.style.display = "none";
    };

    // Any drag that ends or is dropped anywhere clears the dim + indicator,
    // even when the drop lands outside the editor or the drag is cancelled.
    useEffect(() => {
      const clear = () => {
        if (editorRef.current) editorRef.current.style.opacity = "";
        if (dropCaretRef.current) dropCaretRef.current.style.display = "none";
      };
      window.addEventListener("dragend", clear);
      window.addEventListener("drop", clear);
      return () => {
        window.removeEventListener("dragend", clear);
        window.removeEventListener("drop", clear);
      };
    }, []);

    // A collapsed caret Range often reports an all-zero getBoundingClientRect
    // in Chrome, so measure an adjacent character instead to get the real
    // on-screen position and line height.
    const caretScreenRect = (
      range: Range
    ): { left: number; top: number; height: number } | null => {
      const node = range.startContainer;
      const offset = range.startOffset;
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node as Text;
        const r = document.createRange();
        if (offset < text.length) {
          r.setStart(text, offset);
          r.setEnd(text, offset + 1);
          const rc = r.getBoundingClientRect();
          if (rc.height)
            return { left: rc.left, top: rc.top, height: rc.height };
        }
        if (offset > 0) {
          r.setStart(text, offset - 1);
          r.setEnd(text, offset);
          const rc = r.getBoundingClientRect();
          if (rc.height)
            return { left: rc.right, top: rc.top, height: rc.height };
        }
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const kids = el.childNodes;
        if (kids.length === 0) {
          const rc = el.getBoundingClientRect();
          return { left: rc.left + 4, top: rc.top + 4, height: 18 };
        }
        const atEnd = offset >= kids.length;
        const refNode = (atEnd ? kids[kids.length - 1] : kids[offset]) as any;
        const rc =
          refNode?.getBoundingClientRect?.() ||
          (() => {
            const rr = document.createRange();
            rr.selectNode(refNode);
            return rr.getBoundingClientRect();
          })();
        if (rc && (rc.height || rc.width))
          return {
            left: atEnd ? rc.right : rc.left,
            top: rc.top,
            height: rc.height || 18,
          };
      }
      const rc = range.getBoundingClientRect();
      if (rc.height || rc.width)
        return { left: rc.left, top: rc.top, height: rc.height || 18 };
      return null;
    };

    // Draw the insertion line at the text position under the cursor so the user
    // sees exactly where the placeholder will land.
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      // Mark this as a move so the browser shows a plain cursor instead of the
      // boxed copy/"+" drag badge.
      e.dataTransfer.dropEffect = "move";
      const caret = dropCaretRef.current;
      const wrapper = wrapperRef.current;
      const range = caretRangeFromPoint(e.clientX, e.clientY);
      if (
        !caret ||
        !wrapper ||
        !range ||
        !editorRef.current?.contains(range.commonAncestorContainer)
      ) {
        hideDropCaret();
        return;
      }
      const rect = caretScreenRect(range);
      if (!rect) {
        hideDropCaret();
        return;
      }
      const wrect = wrapper.getBoundingClientRect();
      // Anchor the line to the insertion row, lifted slightly so it reads above
      // the text. The dragged ghost is offset below the cursor (see the
      // dragstart handlers) so it no longer covers this line.
      const lift = 6;
      setDimmed(true);
      caret.style.display = "block";
      caret.style.left = `${rect.left - wrect.left}px`;
      caret.style.top = `${rect.top - wrect.top - lift}px`;
      caret.style.height = `${rect.height + lift}px`;
    };

    // Used when a drop lands outside any text node (e.g. empty editor).
    const insertViaHandleFallback = (tag: string, commit = true) => {
      const el = editorRef.current;
      if (!el) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      insertChipAtRange(tag, range, commit);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      hideDropCaret();
      setDimmed(false);
      const moveTag = e.dataTransfer.getData("text/placeholder-move");
      const insertTag = e.dataTransfer.getData("text/placeholder");
      const tag = moveTag || insertTag;
      if (!tag) return;
      e.preventDefault();

      // A move is placement + removal; commit once so Ctrl+Z undoes the whole
      // move in a single step instead of leaving an in-between state.
      const isMove = !!moveTag;
      const range = caretRangeFromPoint(e.clientX, e.clientY);
      if (range && editorRef.current?.contains(range.commonAncestorContainer))
        insertChipAtRange(tag, range, !isMove);
      else insertViaHandleFallback(tag, !isMove);

      if (isMove && draggedChipRef.current) {
        draggedChipRef.current.remove();
        pushChange();
      }
      draggedChipRef.current = null;
    };

    const placeholderText = useMemo(() => placeholder || "", [placeholder]);

    return (
      <div className={`flex flex-col gap-1.5 ${className || ""}`}>
        <div className="flex flex-wrap items-center gap-1 rounded border border-themed-borderSecondary bg-themed-card px-1.5 py-1">
          {MARKDOWN_TOOLS.map((tool) => (
            <button
              key={tool.action}
              type="button"
              title={tool.title}
              aria-label={tool.title}
              onMouseDown={(e) => {
                e.preventDefault();
                saveSelection();
              }}
              onClick={() => applyMarkdown(tool.action)}
              className={`h-7 min-w-[28px] rounded border border-themed-borderSecondary px-2 text-[12px] font-semibold text-themed-text hover:bg-themed-background focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 ${
                tool.action === "italic" ? "italic" : ""
              }`}
            >
              {tool.label}
            </button>
          ))}
        </div>
        <div ref={wrapperRef} className="relative flex-1 min-h-0">
          {isEmpty && placeholderText && (
            <div className="absolute top-2 left-3 right-3 text-[13px] text-themed-textSecondary pointer-events-none">
              {placeholderText}
            </div>
          )}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onKeyDown={handleKeyDown}
            onInput={pushChange}
            onBlur={saveSelection}
            onKeyUp={saveSelection}
            onMouseUp={saveSelection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={hideDropCaret}
            onDragEnd={hideDropCaret}
            onDrop={handleDrop}
            className="w-full h-full overflow-y-auto whitespace-pre-wrap px-3 py-2 border border-themed-borderSecondary rounded text-[13px] leading-[20px] focus:outline-none focus:border-[#7C3AED] font-inter"
          />
          {/* Insertion line, rendered last and elevated so it sits on top of the
              chips. Styled like the native text caret: thin, theme text color,
              blinking. */}
          <div
            ref={dropCaretRef}
            className="drop-caret absolute w-px pointer-events-none z-50"
            style={{ display: "none", backgroundColor: "var(--foreground)" }}
          />
        </div>
      </div>
    );
  }
);

AiPromptEditor.displayName = "AiPromptEditor";

export default AiPromptEditor;
